import { z } from 'zod';
import { CreditApplication, PricingAggregates } from '~/contracts/flp';
import { logError } from '~/utilities/loggers';
import showApiError from './showApiError';

/**
 * Zod schemas for validating financing data
 */

const ApplicantSchema = z.object({
  firstName: z.string(),
  middleName: z.string().optional(),
  lastName: z.string(),
  address: z
    .object({
      street: z.string(),
      city: z.string(),
      state: z.string(),
      zip: z.string()
    })
    .nullable()
});

const CoApplicantSchema = z.object({
  firstName: z.string(),
  lastName: z.string()
});

const DownPaymentAggregateSchema = z.object({
  downPayment: z.number(),
  count: z.number()
});

const QualifiedVehicleDetailsSchema = z.object({
  apr: z.number(),
  downPayment: z.number(),
  monthlyPayment: z.number(),
  stickerPrice: z.number(),
  term: z.number(),
  vehicleId: z.number()
});

const TradeInDetailsSchema = z.object({
  applyTradeIn: z.boolean(),
  appraisalId: z.number(),
  make: z.string(),
  mobileImage: z.string(),
  model: z.string(),
  status: z
    .string()
    .transform(val => val.toLowerCase())
    .pipe(
      z.union([
        z.literal('active'),
        z.literal('completed'),
        z.literal('deleted'),
        z.literal('expired'),
        z.literal('notset'),
        z.literal('rejected'),
        z.literal('unclaimed')
      ])
    ),
  totalTradeInValue: z.number(),
  trim: z.string(),
  year: z.number()
});

const VehicleAggregateSchema = z.object({
  count: z.number(),
  minimumMonthlyPayment: z.number(),
  name: z
    .string()
    .transform(val => val.toLowerCase())
    .pipe(
      z.union([
        z.literal('convertible'),
        z.literal('coupe'),
        z.literal('hatchback'),
        z.literal('minivan'),
        z.literal('pickup'),
        z.literal('sedan'),
        z.literal('suv'),
        z.literal('wagon')
      ])
    )
});

const PricingAggregatesSchema = z.object({
  error: z.any().optional(),
  financeInfo: z.object({
    coApplicantEligibility: z.object({
      coBorrowerAllowed: z.boolean(),
      coSignerAllowed: z.boolean(),
      stateAbbreviation: z.string()
    }),
    coApplicantToggleOn: z.boolean(),
    financingType: z.union([z.literal('userFinancing'), z.literal('expired')]),
    hasCoApplicant: z.boolean(),
    maxMonthlyPayment: z.number(),
    minDownPayment: z.number()
  }),
  bodyStyles: z.array(VehicleAggregateSchema),
  downPaymentBuckets: z.array(DownPaymentAggregateSchema),
  minDownVehicle: QualifiedVehicleDetailsSchema,
  minPaymentVehicle: QualifiedVehicleDetailsSchema,
  totalMatchingInventory: z.number(),
  tradeInfo: TradeInDetailsSchema.optional()
});

const CreditStatusSchema = z.object({
  hasValidSoftPull: z.boolean(),
  hasErroredSoftPull: z.boolean(),
  hasExpiredSoftPull: z.boolean()
});

const FinancePolicySchema = z.object({
  minMinDown: z.number(),
  startApr: z.number(),
  maxTerm: z.number(),
  maxPayment: z.number(),
  totalDown: z.number().optional()
});

const CreditApplicationSchema = z.object({
  applicant: ApplicantSchema.nullable(),
  baseFinancingPolicy: FinancePolicySchema.optional(),
  coapplicant: CoApplicantSchema.nullable().optional(),
  coApplicantEligibility: z.object({
    stateAbbreviation: z.string(),
    coBorrowerAllowed: z.boolean(),
    coSignerAllowed: z.boolean()
  }),
  coApplicantToggleOn: z.boolean(),
  creditApplicationExpirationUtc: z.string(),
  creditApplicationId: z.string(),
  creditApplicationPricingUtc: z.string(),
  creditStatus: CreditStatusSchema,
  error: z.string().nullable().optional(),
  financingPolicy: FinancePolicySchema.optional(),
  hasCoApplicant: z.boolean(),
  hasTradeIn: z.boolean()
});

/**
 * Validates data against a Zod schema and logs errors
 */
function validateAndLog<T>(
  data: unknown,
  schema: z.ZodType<T>,
  dataType: string
): boolean {
  const result = schema.safeParse(data);

  if (!result.success) {
    const validationErrors = result.error.errors.map(error => ({
      path: error.path.join('.'),
      message: error.message,
      code: error.code
    }));

    logError(
      new Error(
        `[FLP Validation Error] ${dataType} received data with validation errors`
      ),
      { location: `FLP Validation - ${dataType}`, validationErrors }
    );
  }

  return result.success;
}

/**
 * Validates creditApplication and pricingAggregates data before processing
 * in the FinancingContext
 */
export default function validateFinancingData(
  creditApplication: CreditApplication,
  pricingAggregates?: PricingAggregates
): void {
  const creditApplicationIsValid = validateAndLog(
    creditApplication,
    CreditApplicationSchema,
    'creditApplication'
  );
  let pricingAggregatesIsValid = true;

  if (pricingAggregates) {
    pricingAggregatesIsValid = validateAndLog(
      pricingAggregates,
      PricingAggregatesSchema,
      'pricingAggregates'
    );
  }

  if (!creditApplicationIsValid || !pricingAggregatesIsValid) {
    showApiError();
  }
}
